import { ToastrService } from 'ngx-toastr';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ENTER, COMMA } from '@angular/cdk/keycodes';
import {
  Component,
  Inject,
  OnDestroy,
  AfterViewInit,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnUser } from '@lib/api/entities';
import { SnIncidentsAPI, SnUsersAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { GoogleMap, GoogleMapLayer, GoogleMapStyle } from '@lib/geo/google/map';
import { SnLonLatLike } from '@lib/geo/helpers';
import { t } from '@lib/translation/helpers/translation.helper';
import { MediaMessageVaultsDialogComponent } from '@lib/vault';

import { SnChannel } from '../../../models';
import { SnChannelsService } from '../../../services/channels.service';
import { SnMessagesService } from '../../../services/messages.service';
import { ChannelsToShareDialogComponent } from '../../channels-to-share-dialog/channels-to-share-dialog.component';
import { CanvasComponent } from '../canvas/canvas.component';

@Component({
  selector: 'sn-telestrate-dialog',
  templateUrl: './telestrate-dialog.component.html',
  styleUrls: ['./telestrate-dialog.component.scss'],
})
export class TelestrateDialogComponent implements OnDestroy, AfterViewInit {
  @ViewChild(CanvasComponent) public CanvasComponent: CanvasComponent;
  public isCanvas: boolean = false;
  public canvas: HTMLCanvasElement;
  public canvasMap: HTMLCanvasElement;
  public isSending: boolean = false;
  public mapStyle: GoogleMapStyle;
  public _map: GoogleMap;
  public croppedPhoto: HTMLCanvasElement;
  public height: number;
  public width: number;
  public croppedUrl: string;
  public loading: boolean = false;

  public telestrationTitle: string = `telestration-${new Date().getTime()}`;

  @Output() public readonly editedCanvas = new EventEmitter<HTMLCanvasElement>(null);
  @Output() public readonly deactivateTelestration = new EventEmitter<void>();

  public cropper: Cropper;
  public readonly googleMapFloorLayer: GoogleMapLayer;
  public readonly googleMapCameraLayer: GoogleMapLayer;
  public googleMapCenter: SnLonLatLike;
  public form: UntypedFormGroup = new UntypedFormGroup({
    users: new UntypedFormControl(),
    teams: new UntypedFormControl(),
  });
  public formFields: SnFormFieldConfig[] = [
    {
      fieldGroupClassName: 'telestrate-dialog__row',
      fieldGroup: [
        {
          key: 'channel',
          type: 'http-select',
          className: 'telestrate-dialog__col',
          templateOptions: {
            label: 'Channel',
            valueProp: 'sid',
            labelProp: 'name',
            emptyLabel: 'Create New Channel',
            fetchDataFn: () => this._channels.getChannelList(),
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'telestrate-dialog__row',
      hideExpression: () => !this.form.dirty || this.form.value.channel,
      fieldGroup: [
        {
          key: 'name',
          type: 'input',
          className: 'telestrate-dialog__col',
          templateOptions: {
            label: 'Channel Name',
            required: true,
          },
        },
        {
          key: 'users',
          type: 'chips-autocomplete',
          className: 'telestrate-dialog__col',
          templateOptions: {
            labelProp: (u) => `${u.firstName} ${u.lastName}`,
            label: 'Start typing a user',
            fetchDataFn: this._fetchUserData.bind(this),
            separatorKeysCodes: [ENTER, COMMA],
            required: true,
          },
        },
      ],
    },
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    public currentOrganization: SnCurrentOrganization,
    private readonly _dialogRef: MatDialogRef<TelestrateDialogComponent>,
    private readonly _channels: SnChannelsService,
    private readonly _messages: SnMessagesService,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _ability: SnAbilityService,
    private readonly _toastrService: ToastrService,
    private readonly _dialog: MatDialog,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _incidentsAPI: SnIncidentsAPI,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      map?: GoogleMap;
      shapes?: any[];
      channel?: SnChannel;
      canvas?: HTMLCanvasElement;
    },
  ) {
    if (!this.data.channel?.id) {
      this.googleMapFloorLayer = new GoogleMapLayer();
      this.googleMapCameraLayer = new GoogleMapLayer();
      this.googleMapCenter = this.data.map.getCenter();
    }

    HTMLCanvasElement.prototype.getContext = this._webGLDrawingBuffer(
      HTMLCanvasElement.prototype.getContext,
    );

    this.canvas = this.data.canvas;
    if (this.canvas) {
      this.isCanvas = true;
    }
  }

  public ngAfterViewInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public download(): void {
    this.CanvasComponent.combineKonvaCanvas()
      .pipe(takeUntil(this._destroy$))
      .subscribe((canvas) => {
        canvas.toBlob((blob) => {
          const downloadLink = document.createElement('a');
          downloadLink.setAttribute('download', `${this.telestrationTitle}`);
          const url = URL.createObjectURL(blob);
          downloadLink.setAttribute('href', url);
          downloadLink.click();
        });
      });
  }

  public saveToVault(): void {
    this.CanvasComponent.combineKonvaCanvas()
      .pipe(takeUntil(this._destroy$))
      .subscribe((canvas) => {
        this._toBlobCallBack(canvas);
      });
  }

  public canSend(): boolean {
    return (
      this._ability.can(
        {
          subject: 'communications/chatchannel',
          action: ['add'],
        },
        {
          subject: 'communications/chatuser',
          action: ['view'],
        },
      ) && this._currentOrganization.settings.enableCommunicationServices
    );
  }

  public zoomIn(): void {
    this._map.setZoom(this._map.getZoom() + 1);
  }

  public zoomOut(): void {
    this._map.setZoom(this._map.getZoom() - 1);
  }

  public share(): void {
    this.isSending = true;
    const dialogRef = this._dialog.open(ChannelsToShareDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (channel?.twilioChannel) {
          this._send(channel);
        } else {
          this.isSending = false;
        }
      });
  }

  public shareToSame(): void {
    this.isSending = true;
    this._send(this.data.channel);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private async _toBlobCallBack(canvas: HTMLCanvasElement): Promise<void> {
    const blobFile = await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file: any = blob;
        file.lastModifiedDate = new Date();
        file.name = `${this.telestrationTitle}.jpeg`;
        resolve(file);
      });
    });
    this._sendToVaultDialog(blobFile);
  }

  private _send(channel: SnChannel): void {
    this.CanvasComponent.combineKonvaCanvas()
      .pipe(takeUntil(this._destroy$))
      .subscribe((canvas) => {
        // send the message
        canvas.toBlob((blob) => {
          const now = new Date();
          const name = `${now.getTime()}`;
          const file: any = blob;
          file.lastModifiedDate = new Date();
          file.name = `telestration-${name}.jpeg`;
          this._sendMessage(channel, file);
        });
      });
  }

  private _sendMessage(channel: SnChannel, file: File): void {
    this._messages
      .sendMediaMessage(channel, file)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._dialogRef.close();
          this._toastrService.success(t('communication.telestration.sent'));
        },
        error: (error) => {
          console.error(error);
          this._toastrService.error(t('common.failedToSend'));
        },
      });
  }

  private _fetchUserData(params: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    return this._usersAPI.getUserList(params);
  }

  private _webGLDrawingBuffer(originalContext: any): any {
    return function (type: unknown, attribs: any): any {
      attribs = attribs || {};
      attribs.preserveDrawingBuffer = true;
      return originalContext.call(this, type, attribs);
    };
  }

  private _sendToVaultDialog(file: any): void {
    this.loading = true;
    const dialog = this._dialog.open(MediaMessageVaultsDialogComponent, {
      data: {
        file,
        needsUpload: true,
      },
      width: '700px',
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        if (res) {
          this._submitVaultIncidentMedia(res.id.id, file, res.closeTelestration);
        }
        this.loading = false;
      });
  }

  private _transformToFormData(file: Blob): FormData {
    const fileName = `${this.telestrationTitle}.jpeg`;

    const formData = new FormData();
    formData.append('files', file, fileName);

    return formData;
  }

  private _submitVaultIncidentMedia(id: string, file: any, close: boolean): void {
    this._incidentsAPI
      .createVaultIncidentMedia(id, this._transformToFormData(file))
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.loading = false;
        if (close) {
          // emits an event to close the telestration when 'save and close' is clicked
          this.deactivateTelestration.emit();
          this._dialogRef.close();
        }
      });
  }
}
